import { type TestId } from '@repo/test-id';
import classNames from 'classnames';
import { type ReactNode } from 'react';

interface Props {
  selectedMethod: boolean;
  onMethodSelection: () => void;
  icon: ReactNode;
  title: string;
  subtitle?: string;
  testId: TestId;
  children?: ReactNode;
  actionButtons?: ReactNode;
  disabled?: boolean;
}

export default function PaymentMethodCard({
  selectedMethod,
  onMethodSelection,
  icon,
  title,
  subtitle,
  testId,
  children,
  actionButtons,
  disabled = false,
}: Props) {
  return (
    <label
      className={classNames(
        'flex w-full flex-col overflow-hidden rounded-2xl border-2 p-4',
        'has-focus-visible:ring-brand has-focus-visible:ring-2 has-focus-visible:ring-offset-2',
        disabled ? 'border-stroke-base-medium bg-primary cursor-not-allowed' : '',
        !disabled &&
          (selectedMethod ? 'border-brand bg-brand-subtle' : 'border-stroke-base-medium bg-primary cursor-pointer')
      )}
      data-testid={testId}
    >
      <input
        type="radio"
        checked={selectedMethod}
        onChange={onMethodSelection}
        disabled={disabled}
        className="sr-only"
      />
      <div className="flex items-center gap-3">
        <div
          className={classNames(
            'flex h-5 w-5 items-center justify-center rounded-full border-2',
            selectedMethod ? 'border-brand' : 'border-stroke-base-medium',
            { 'opacity-50': disabled }
          )}
        >
          {selectedMethod && <div className="bg-brand h-2 w-2 rounded-full" />}
        </div>

        <div className="flex flex-1 items-center justify-between">
          <div className={classNames('flex flex-col gap-0.5', { 'opacity-50': disabled })}>
            <div className="text-heading flex items-center gap-2 text-base leading-6 font-medium">{title}</div>
            {subtitle && <div className="text-heading text-sm leading-5">{subtitle}</div>}
          </div>
          <div className="mb-0.5 flex items-center">
            {/* Action buttons stay usable on a disabled method — e.g. topping up a wallet that can't cover the price. */}
            {actionButtons}
            <div className={classNames({ 'opacity-50': disabled })}>{icon}</div>
          </div>
        </div>
      </div>
      <div
        className={classNames('grid w-full transition-all duration-300 ease-in-out', {
          'grid-rows-[1fr] opacity-100': selectedMethod && children,
          'grid-rows-[0fr] opacity-0': !selectedMethod || !children,
        })}
      >
        <div className="overflow-hidden">{children && <div className="mt-4">{children}</div>}</div>
      </div>
    </label>
  );
}
